import { useEffect, useMemo, useState } from "react";
import { showAlert } from "../utils/Helper";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";

const EMPTY_FORM = {
  NameTitleID: "",
  MemberName: "",
  DesignationID: "",
  FNameTitleID: "",
  FName: "",
  Gender: "M",
  Country: "91",
  Mobile: "",
  DOB: "",
  ValidityDate: "",
  EmpType: "",
  EMail: "",
  PermanentAddress: "",
  PermanentPIN: "",
  PermanentStateID: "",
  PermanentCityID: "",
  CollegeID: "",
  ACollege: "",
  IDCardNo: ""
};

const ALLOWED_TEACHER_DESIGNATION_IDS = new Set([1, 4, 5, 28, 29, 31, 37]);

const MAX_ID_PROOF_KB = 200;

function compressImage(imageFile, maxKB) {
  return new Promise((resolve) => {
    const maxBytes = maxKB * 1024;
    if (imageFile.size <= maxBytes) { resolve(imageFile); return; }
    const img = new Image();
    const url = URL.createObjectURL(imageFile);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.85;
      const tryNext = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(imageFile); return; }
          if (blob.size <= maxBytes || quality <= 0.1) {
            resolve(new File([blob], imageFile.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
          } else {
            quality = Math.max(0.1, quality - 0.1);
            tryNext();
          }
        }, "image/jpeg", quality);
      };
      tryNext();
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(imageFile); };
    img.src = url;
  });
}

function toTitleCase(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CollegeTeacherRegistration() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [titles, setTitles] = useState([]);
  const [fatherTitles, setFatherTitles] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [designations, setDesignations] = useState([]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [titlesRes, statesRes, collegesRes, designationsRes] = await Promise.all([
          fetch(`${API_BASE}/meta/name-titles`),
          fetch(`${API_BASE}/states`),
          fetch(`${API_BASE}/meta/colleges`),
          fetch(`${API_BASE}/meta/designations`)
        ]);

        const [titlesData, statesData, collegesData, designationsData] = await Promise.all([
          titlesRes.json(),
          statesRes.json(),
          collegesRes.json(),
          designationsRes.json()
        ]);

        const allTitles = Array.isArray(titlesData) ? titlesData : [];

        const EXCLUDED_TITLE_IDS = new Set([7, 15, 16, 17, 18, 20]);
        setTitles(allTitles.filter((item) => !EXCLUDED_TITLE_IDS.has(Number(item.NameTitleID))));
        setFatherTitles(allTitles);
        setStates(Array.isArray(statesData) ? statesData : []);
        setColleges(Array.isArray(collegesData) ? collegesData : []);
        setDesignations(
          (Array.isArray(designationsData) ? designationsData : []).filter((d) =>
            ALLOWED_TEACHER_DESIGNATION_IDS.has(Number(d.DesignationID))
          )
        );
      } catch {
        setTitles([]);
        setFatherTitles([]);
        setStates([]);
        setColleges([]);
        setDesignations([]);
      }
    };

    loadMasters();
  }, []);

  useEffect(() => {
    const stateId = form.PermanentStateID;
    if (!stateId) {
      setCities([]);
      setForm((prev) => ({ ...prev, PermanentCityID: "" }));
      return;
    }

    const loadCities = async () => {
      try {
        const res = await fetch(`${API_BASE}/cities?stateId=${encodeURIComponent(stateId)}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch {
        setCities([]);
      }
    };

    loadCities();
  }, [form.PermanentStateID]);

  const todayMaxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().slice(0, 10);
  }, []);

  const validityDateTooltip =
    form.EmpType === "P"
      ? "Date of Retirement for Permanent Employee"
      : form.EmpType === "C"
        ? "Term of Appointment for Contractual Teacher"
        : "Date of Retirement for Permanent Employee / Term of Appointment for Contractual Teacher";

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["Mobile", "PermanentPIN", "Country"].includes(name)) {
      if (!/^\d*$/.test(value)) return;
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (["MemberName", "FName"].includes(name)) {
      const filtered = value.replace(/[^a-zA-Z\s.]/g, "");
      setForm((prev) => ({ ...prev, [name]: toTitleCase(filtered) }));
      return;
    }

    if (name === "EMail") {
      setForm((prev) => ({ ...prev, [name]: value.toLowerCase().trim() }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCollegeChange = (e) => {
    const selectedCollegeId = e.target.value;
    const selectedCollege = colleges.find((c) => String(c.CollegeID) === String(selectedCollegeId));
    setForm((prev) => ({
      ...prev,
      CollegeID: selectedCollegeId,
      ACollege: selectedCollege?.CollegeDisplay || ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = [
      "NameTitleID",
      "MemberName",
      "DesignationID",
      "FNameTitleID",
      "FName",
      "Gender",
      "Country",
      "Mobile",
      "DOB",
      "EMail",
      "PermanentAddress",
      "PermanentPIN",
      "PermanentStateID",
      "PermanentCityID",
      "CollegeID",
      "IDCardNo"
    ];

    for (const field of required) {
      if (!String(form[field] || "").trim()) {
        showAlert("All mandatory fields must be filled", null, "error");
        return;
      }
    }

    // Country code: 1–4 digits
    if (!/^\d{1,4}$/.test(String(form.Country || "").trim())) {
      showAlert("Country code must be 1 to 4 digits", null, "error");
      return;
    }

    // Mobile: exactly 10 digits, not all same digit
    const mobile = String(form.Mobile || "").replace(/^0+/, "");
    if (!/^\d{10}$/.test(mobile)) {
      showAlert("Mobile must be exactly 10 digits", null, "error");
      return;
    }
    if (/^(\d)\1{9}$/.test(mobile)) {
      showAlert("Please enter a valid mobile number", null, "error");
      return;
    }

    // Email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.EMail || "")) {
      showAlert("Invalid email format.", null, "error");
      return;
    }

    // Name: at least 2 letters
    if (String(form.MemberName || "").replace(/[^a-zA-Z]/g, "").length < 2) {
      showAlert("Name must contain at least 2 letters", null, "error");
      return;
    }

    // Father Name: at least 2 letters
    if (String(form.FName || "").replace(/[^a-zA-Z]/g, "").length < 2) {
      showAlert("Father Name must contain at least 2 letters", null, "error");
      return;
    }

    // PIN: exactly 6 digits
    if (!/^\d{6}$/.test(form.PermanentPIN || "")) {
      showAlert("PIN must be exactly 6 digits", null, "error");
      return;
    }

    // Gender
    if (!["M", "F", "O"].includes(String(form.Gender || "").toUpperCase())) {
      showAlert("Invalid gender value.", null, "error");
      return;
    }

    // Common today reference (midnight)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // DOB: valid date and age >= 18
    const dob = new Date(form.DOB);
    if (Number.isNaN(dob.getTime())) {
      showAlert("Invalid date of birth", null, "error");
      return;
    }
    const minDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    if (dob > minDob) {
      showAlert("Age must be at least 18 years", null, "error");
      return;
    }

    // Validity Date: required and must be future when Employee Type is P or C
    if (["P", "C"].includes(form.EmpType)) {
      if (!form.ValidityDate) {
        showAlert("Validity Date is required for the selected Employee Type", null, "error");
        return;
      }
      const validityDate = new Date(form.ValidityDate);
      if (validityDate <= today) {
        showAlert("Validity Date must be a future date", null, "error");
        return;
      }
    }

    // Identity Card No: at least 3 characters
    if (String(form.IDCardNo || "").trim().length < 3) {
      showAlert("Identity Card No. must be at least 3 characters", null, "error");
      return;
    }

    if (!file) {
      showAlert("Official ID proof is required.", null, "error");
      return;
    }

    const allowed = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowed.includes(file.type)) {
      showAlert("Only JPG, JPEG, PNG files are allowed", null, "error");
      return;
    }

    let uploadFile = file;
    if (file.type.startsWith("image/") && file.size > MAX_ID_PROOF_KB * 1024) {
      uploadFile = await compressImage(file, MAX_ID_PROOF_KB);
    }

    if (uploadFile.size > MAX_ID_PROOF_KB * 1024) {
      showAlert(`Official ID proof must be less than ${MAX_ID_PROOF_KB} KB. Please use a smaller file.`, null, "error");
      return;
    }

    const payload = new FormData();
    Object.entries({ ...form, Mobile: mobile }).forEach(([k, v]) => payload.append(k, String(v || "")));
    payload.append("SupportDoc", uploadFile);

    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/college-teachers/register`, {
        method: "POST",
        body: payload
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Registration failed");
      }

      showAlert(data?.message || "Registration successful", "/login", "success");
      setForm(EMPTY_FORM);
      setFile(null);
    } catch (err) {
      showAlert(err.message || "Registration failed", null, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="container-fluid" style={{ textAlign: "left", marginTop: "10px" }}>
      <h3 className="ctitle">Registration (Teacher of Affiliated College)</h3>
      <div className="col-lg-10">
        <form onSubmit={handleSubmit} encType="multipart/form-data">

          {/* Row 1: Title / Name / Gender / Designation / Father Title / Father Name */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">Title</label>
              <select className="form-control" name="NameTitleID" value={form.NameTitleID} onChange={handleChange} required>
                <option value="">Select</option>
                {titles.map((t) => (
                  <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">Name</label>
              <input className="form-control" name="MemberName" value={form.MemberName} onChange={handleChange} placeholder="Name...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Gender</label>
              <select className="form-control" name="Gender" value={form.Gender} onChange={handleChange} required>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Others</option>
              </select>
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Designation</label>
              <select className="form-control" name="DesignationID" value={form.DesignationID} onChange={handleChange} required>
                <option value="">Select Designation</option>
                {designations.map((d) => (
                  <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">Father Title</label>
              <select className="form-control" name="FNameTitleID" value={form.FNameTitleID} onChange={handleChange} required>
                <option value="">Select</option>
                {fatherTitles.map((t) => (
                  <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">Father Name</label>
              <input className="form-control" name="FName" value={form.FName} onChange={handleChange} placeholder="Father Name...*" required />
            </div>
          </div>

          {/* Row 2: CountryCode / Mobile / DOB / Validity Date / Employee Type / EMail */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-1">
              <label className="form-label mb-1">CountryCode</label>
              <input className="form-control" name="Country" value={form.Country} onChange={handleChange} required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Mobile (10 digits)*</label>
              <input
                className="form-control"
                name="Mobile"
                value={form.Mobile}
                onChange={handleChange}
                maxLength={10}
                minLength={10}
                inputMode="numeric"
                pattern="\d{10}"
                title="Enter exactly 10 digits"
                required
              />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">DOB</label>
              <input className="form-control" type="date" name="DOB" value={form.DOB} onChange={handleChange} max={todayMaxDob} required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1" title={validityDateTooltip}>
                Validity Date
                <i className="bi bi-info-circle ms-1 text-muted" style={{ fontSize: "0.8rem" }} title={validityDateTooltip}></i>
              </label>
              <input className="form-control" type="date" name="ValidityDate" value={form.ValidityDate} onChange={handleChange} />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Employee Type</label>
              <select className="form-control" name="EmpType" value={form.EmpType} onChange={handleChange}>
                <option value="">Select</option>
                <option value="C">Contractual</option>
                <option value="P">Permanent</option>
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">EMail</label>
              <input className="form-control" type="email" name="EMail" value={form.EMail} onChange={handleChange} placeholder="EMail...*" required />
            </div>
          </div>

          {/* Row 3: Permanent Address / PIN / State / City */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-5">
              <label className="form-label mb-1">Permanent Address</label>
              <input className="form-control" name="PermanentAddress" value={form.PermanentAddress} onChange={handleChange} placeholder="Permanent Address...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">PIN</label>
              <input className="form-control" name="PermanentPIN" value={form.PermanentPIN} onChange={handleChange} maxLength={6} placeholder="PIN...*" required />
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">State</label>
              <select className="form-control" name="PermanentStateID" value={form.PermanentStateID} onChange={handleChange} required>
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.StateID} value={s.StateID}>{s.State}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">City</label>
              <select className="form-control" name="PermanentCityID" value={form.PermanentCityID} onChange={handleChange} required>
                <option value="">Select City</option>
                {cities.map((c) => (
                  <option key={c.CityID} value={c.CityID}>{c.City}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 4: Affiliated College / Identity Card No / Official ID Proof */}
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-12 col-sm-6">
              <label className="form-label mb-1">Affiliated College Name</label>
              <select className="form-control" name="CollegeID" value={form.CollegeID} onChange={handleCollegeChange} required>
                <option value="">Select Affiliated College</option>
                {colleges.map((college) => (
                  <option key={college.CollegeID} value={college.CollegeID}>{college.CollegeDisplay}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Identity Card No.</label>
              <input className="form-control" name="IDCardNo" value={form.IDCardNo} onChange={handleChange} placeholder="ID Card No...*" required />
            </div>

            <div className="col-12 col-sm-4">
              <label
                className="form-label mb-1"
                title={`Allowed formats: JPG, JPEG, PNG only. Max ${MAX_ID_PROOF_KB}KB. Images above ${MAX_ID_PROOF_KB}KB are auto-compressed.`}
              >
                Official ID Proof (max {MAX_ID_PROOF_KB}KB)
                <i className="bi bi-info-circle ms-1 text-muted" style={{ fontSize: "0.85rem" }}></i>
              </label>
              <input className="form-control" type="file" accept=".png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Now"}
              </button>
            </div>
          </div>

        </form>
      </div>
    </section>
  );
}
